import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { servicesService, serviceBidsService } from '../services/api';

interface Service {
  id: number;
  title: string;
  description: string;
  category: string;
  location: string;
  budget: number;
  currency: string;
  deadline: string;
  requester_name: string;
  status: string;
  is_urgent: boolean;
  bid_count: number;
  created_at: string;
  requirements?: string[];
}

const BrowseServicesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    status: 'open',
    location: '',
    budget_min: '',
    budget_max: ''
  });
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [bidAmount, setBidAmount] = useState('');
  const [bidProposal, setBidProposal] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState('');
  const [submittingBid, setSubmittingBid] = useState(false);

  const isServiceProvider = user?.roles?.some(role => role.name === 'service_provider')
  
  useEffect(() => {
    loadServices();
  }, [filters]);

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);

      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );

      const response = await servicesService.getServices({
        ...cleanFilters,
        budget_min: cleanFilters.budget_min ? parseFloat(cleanFilters.budget_min) : undefined,
        budget_max: cleanFilters.budget_max ? parseFloat(cleanFilters.budget_max) : undefined,
        limit: 50
      });

      setServices(response.data || []);
    } catch (error) {
      console.error('Error loading services:', error);
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      loadServices();
      return;
    }

    try {
      setLoading(true);
      const response = await servicesService.searchServices(searchTerm, {
        category: filters.category || undefined,
        status: filters.status || undefined,
        limit: 50
      });
      setServices(response.data || []);
    } catch (error) {
      console.error('Error searching services:', error);
      setError('Failed to search services');
    } finally {
      setLoading(false);
    }
  };

  const submitBid = async (serviceId: number) => {
    if (!bidAmount || !bidProposal) {
      alert('Please fill in bid amount and proposal');
      return;
    }

    try {
      setSubmittingBid(true);
      await serviceBidsService.createBid({
        service_id: serviceId.toString(),
        bid_amount: parseFloat(bidAmount),
        proposal: bidProposal,
        estimated_duration: estimatedDuration
      });

      alert('Bid submitted successfully!');
      setSelectedService(null);
      setBidAmount('');
      setBidProposal('');
      setEstimatedDuration('');
      loadServices(); // Refresh to update bid count
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to submit bid');
    } finally {
      setSubmittingBid(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'KSH') => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: currency === 'KSH' ? 'KES' : currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string, isUrgent: boolean = false) => {
    const baseClasses = "px-2 py-1 rounded text-xs font-medium";
    const urgentClass = isUrgent ? " border-2 border-red-400" : "";
    
    switch (status) {
      case 'open':
        return `${baseClasses} bg-green-100 text-green-800${urgentClass}`;
      case 'assigned':
        return `${baseClasses} bg-blue-100 text-blue-800${urgentClass}`;
      case 'completed':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 'cancelled':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800${urgentClass}`;
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons: { [key: string]: string } = {
      'cleaning': '🧹',
      'repair': '🔧',
      'delivery': '📦',
      'tutoring': '📚',
      'design': '🎨',
      'writing': '✍️',
      'programming': '💻',
      'consulting': '💼',
      'fitness': '💪',
      'beauty': '💄',
      'photography': '📸',
      'translation': '🌐',
      'legal': '⚖️',
      'accounting': '💰',
      'marketing': '📢',
      'event-planning': '🎉',
      'catering': '🍽️',
      'landscaping': '🌿'
    };
    return icons[category] || '🛠️';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Browse Services</h1>
              <p className="text-gray-600">Find services you need or bid on available requests</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/services/request')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Request Service
              </button>
              <button
                onClick={() => navigate('/services')}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex space-x-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search services..."
                className="flex-1 border border-gray-300 rounded px-3 py-2"
              />
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
              >
                Search
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <select
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                className="border border-gray-300 rounded px-3 py-2"
              >
                <option value="">All Categories</option>
                <option value="cleaning">Cleaning</option>
                <option value="repair">Repair & Maintenance</option>
                <option value="delivery">Delivery</option>
                <option value="tutoring">Tutoring</option>
                <option value="design">Design</option>
                <option value="writing">Writing</option>
                <option value="programming">Programming</option>
                <option value="other">Other</option>
              </select>
              
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="border border-gray-300 rounded px-3 py-2"
              >
                <option value="open">Open</option>
                <option value="assigned">Assigned</option>
                <option value="">All Statuses</option>
              </select>
              
              <input
                type="text"
                value={filters.location}
                onChange={(e) => setFilters({...filters, location: e.target.value})}
                placeholder="Location"
                className="border border-gray-300 rounded px-3 py-2"
              />
              
              <input
                type="number"
                value={filters.budget_min}
                onChange={(e) => setFilters({...filters, budget_min: e.target.value})}
                placeholder="Min Budget"
                className="border border-gray-300 rounded px-3 py-2"
              />
              
              <input
                type="number"
                value={filters.budget_max}
                onChange={(e) => setFilters({...filters, budget_max: e.target.value})}
                placeholder="Max Budget"
                className="border border-gray-300 rounded px-3 py-2"
              />
            </div>
          </form>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
            <button 
              onClick={loadServices}
              className="mt-2 text-sm bg-red-100 hover:bg-red-200 px-3 py-1 rounded"
            >
              Try again
            </button>
          </div>
        ) : services.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No services found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ? 'Try adjusting your search terms' : 'No services match your current filters'}
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilters({
                  category: '',
                  status: 'open',
                  location: '',
                  budget_min: '',
                  budget_max: ''
                });
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="text-2xl">{getCategoryIcon(service.category)}</div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">{service.title}</h3>
                        {service.is_urgent && <span className="text-red-500">🔥</span>}
                      </div>
                      <p className="text-sm text-gray-600">
                        Posted by {service.requester_name} • {service.location} • {formatDate(service.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-green-600 mb-1">
                      {formatCurrency(service.budget, service.currency)}
                    </div>
                    <span className={getStatusBadge(service.status, service.is_urgent)}>
                      {service.status}
                    </span>
                  </div>
                </div>

                <p className="text-gray-700 mb-4 line-clamp-3">{service.description}</p>

                {service.requirements && service.requirements.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Requirements:</h4>
                    <div className="flex flex-wrap gap-2">
                      {service.requirements.map((req, index) => (
                        <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm">
                          {req}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>📂 {service.category}</span>
                    <span>💬 {service.bid_count} bids</span>
                    {service.deadline && (
                      <span>⏰ Deadline: {formatDate(service.deadline)}</span>
                    )}
                  </div>
                  
                  <div className="flex space-x-2">
                    <Link
                      to={`/services/${service.id}`}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm"
                    >
                      View Details
                    </Link>
                    {isServiceProvider && service.status === 'open' && (
                      <button
                        onClick={() => setSelectedService(service)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm"
                      >
                        Submit Bid
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bid Modal */}
      {selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Submit Bid for: {selectedService.title}
              </h3>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-gray-700 mb-2">Service Details</h4>
                <p className="text-sm text-gray-600 mb-2">{selectedService.description}</p>
                <p className="text-sm text-gray-500">
                  Budget: {formatCurrency(selectedService.budget)} • Location: {selectedService.location}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Bid Amount (KSH) *
                  </label>
                  <input
                    type="number"
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="Enter your bid amount"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Duration
                  </label>
                  <input
                    type="text"
                    value={estimatedDuration}
                    onChange={(e) => setEstimatedDuration(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="e.g., 2-3 hours, 1 day, 1 week"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proposal *
                  </label>
                  <textarea
                    value={bidProposal}
                    onChange={(e) => setBidProposal(e.target.value)}
                    rows={4}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="Describe your approach, experience, and why you're the best choice for this service..."
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setSelectedService(null)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => submitBid(selectedService.id)}
                  disabled={submittingBid}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
                >
                  {submittingBid ? 'Submitting...' : 'Submit Bid'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrowseServicesPage;